import * as React from 'react'
import { Container } from '../Container'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AnimateHeight from 'react-animate-height'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { GraySection } from '../GraySection'
import Link from 'next/link'

type Props = {
  title: string
  description?: string | React.ReactElement
  faqs?: { question: string; answer: string | React.ReactElement }[]
}

const faqsItems: {
  question: string
  answer: string | React.ReactElement
}[] = [
  {
    question: 'IS THE INSPECTION REALLY FREE?',
    answer: (
      <div>
        <p>Yes! Whether you meet us at home or at one of our partner locations, the inspection is always 100% free and comes with no obligation.</p>
      </div>
    ),
  },
  {
    question: 'DO I HAVE TO GET MY BRAKES REPAIRED WHEN YOU COME OUT?',
    answer: (
      <div>
        <p>Not at all. You’ll receive a full inspection and a no-obligation quote. If service is needed, you can choose to book a mobile repair or return to our partner location by appointment.</p>
      </div>
    ),
  },
  {
    question: 'DOES NUBRAKES OFFER A WARRANTY?',
    answer: (
      <div>
        <p>
          Yes, all of our repairs are backed by our{' '}
          <Link href={{ pathname: `/warranty` }} target="_blank">
            <a target="_blank">24 month, 24,000 mile warranty</a>
          </Link>
          .
        </p>
        <p>
          We’re committed to providing a transparent, affordable and ultra-convenient brake repair experience, and we
          stand behind our work.
        </p>
      </div>
    ),
  },
  {
    question: 'HOW LONG DOES IT TAKE?',
    answer: (
      <div>
        <p>Your brake inspection will take about 20 minutes to complete. </p>
        <p>A brake repair service will take about 60 minutes to complete. </p>
      </div>
    ),
  },
  {
    question: "WHO ARE NUBRAKES' TECHNICIANS?",
    answer: (
      <div>
        <p>
        Our full-time technicians are experienced professionals with deep expertise in brake systems. They perform inspections and repairs at homes, workplaces, or partner locations, depending on customer preference.
        </p>
      </div>
    ),
  },
  {
    question: 'WHAT TYPE OF PARTS DOES NUBRAKES USE?',
    answer: (
      <div>
        <p>High-quality parts are of the utmost importance when repairing your brakes.</p>
        <p>
          At NuBrakes, most frequently we use professional ceramic or semi-metallic brake pads that are equivalent or
          better than OEM-recommended parts.
        </p>
        <p>
          These parts offer exceptional performance and are backed by our{' '}
          <Link href={{ pathname: `/warranty` }} target="_blank">
            <a target="_blank">24 month, 24,000 mile warranty</a>
          </Link>
          .
        </p>
      </div>
    ),
  },
  {
    question: "Can I Visit NuBrakes in Person?",
    answer: (
      <div>
        <p>
        We have trained NuBrakes representatives based inside select Valvoline Instant Oil Change locations where you can schedule an inspection or consultation. You’ll see NuBrakes signage on the building and our entrance — or you can book a mobile visit online at a location of your choice.
        </p>
      </div>
    ),
  },
]

export const FAQSection: React.FC<Props> = ({ title, description, faqs = faqsItems }) => {
  return (
    <GraySection className="xl:py-20 text-center">
      <Container className="max-w-screen-lg">
        <h2 className="title text-2xl sm:text-3xl md:text-4xl">{title}</h2>
        {description ? <p className="description">{description}</p> : null}
        <div className="grid mt-8 gap-4">
          {faqs.map((value, index) => (
            <FAQ key={index} question={value.question}>
              {value.answer}
            </FAQ>
          ))}
        </div>
      </Container>
    </GraySection>
  )
}

export const FAQ: React.FC<{ question: string; children?: any }> = ({ question, children }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const height = isOpen ? 'auto' : 0
  return (
    <div
      className={`flex flex-col flex-grow rounded-md bg-white border ${
        isOpen ? 'border-[#f04e23]' : 'border-gray-200'
      }`}
    >
      <div className={`text-left`}>
        <div>
          <div className="rounded-md px-4 py-3  h-100" style={{ cursor: 'pointer' }} onClick={() => setIsOpen(!isOpen)}>
            <div className="flex items-center">
              <p className="mb-0 mr-2">
                <strong style={{ fontFamily: 'IndustryInc-Base', fontSize: 14 }}>{question}</strong>
              </p>
              <div className="flex ml-auto">
                <FontAwesomeIcon
                  icon={isOpen ? faChevronUp : faChevronDown}
                  className={`mx-1`}
                  size={'sm'}
                  color="#f04e23"
                />
              </div>
            </div>
          </div>
          <AnimateHeight duration={350} height={height}>
            <div className=" py-4">
              <div>
                <div className="description text-base px-3">{children}</div>
              </div>
            </div>
          </AnimateHeight>
        </div>
      </div>
    </div>
  )
}
